'use client';

import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { sendGTMFormErrorEvent } from '@/components/helpers/analytics/gtm';
import { AtTextAreaFieldProps } from './at-textarea-field.types';

/**
 * `AtTextAreaField` is a controlled textarea component integrated with `react-hook-form`.
 * It handles label rendering, validation errors, accessibility features, and sends
 * GTM tracking events when errors occur.
 *
 * Should be used within a `FormProvider` context like `MlForm`.
 *
 */

const AtTextAreaField = ({
  id,
  label,
  placeholder,
  required,
  className,
  rows = 3,
  defaultValue = '',
}: AtTextAreaFieldProps) => {
  const {
    register,
    formState: { errors, isSubmitting },
    getValues,
  } = useFormContext();

  const hasError = Boolean(errors[id]);
  const errorMessage = hasError ? (errors[id]?.message as string) : '';

  useEffect(() => {
    if (hasError) {
      sendGTMFormErrorEvent({
        form_name: getValues('formName'),
        form_field: id,
        error_message: errorMessage,
      });
    }
  }, [errorMessage, getValues, hasError, id]);

  return (
    <div className={classNames('relative flex w-full flex-col')}>
      {label && (
        <label
          htmlFor={id}
          className={classNames(
            'absolute -top-3 left-4 bg-skin-primary px-1 text-10 text-page-link md:-top-[0.75rem] md:text-sm',
            hasError && 'text-skin-error',
            {
              'after:ml-0.5 after:text-skin-alert after:content-["*"]':
                required,
            },
          )}
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        rows={rows}
        placeholder={placeholder}
        defaultValue={defaultValue}
        {...register(id, {
          required,
          disabled: isSubmitting,
        })}
        className={classNames(
          'w-full rounded-15 border-2 bg-transparent px-4 py-2 focus:outline-none',
          className,
          {
            'border-skin-error focus:outline-red-400': hasError,
            'border-skin-stroke-secondary': !hasError,
          },
        )}
        {...(hasError
          ? {
              'aria-invalid': hasError,
              'aria-describedby': `errorMessage-${id}`,
            }
          : {})}
      />
      {hasError && (
        <p
          className="mt-2 pl-4 text-xs text-skin-alert"
          id={`errorMessage-${id}`}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

AtTextAreaField.displayName = 'AtTextAreaField';

export default AtTextAreaField;
